import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  login as apiLogin,
  logout as apiLogout,
  getAccessToken,
  fetchRolePermission,
  type RolePermission,
} from '@/api/auth'

export const useAuthStore = defineStore('auth', () => {
  // เริ่มจาก token ใน localStorage เพื่อให้ refresh หน้าแล้วยังคง login อยู่
  const token = ref<string | null>(getAccessToken())
  const user = ref<RolePermission | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => !!token.value)
  const roles = computed(() => user.value?.roles ?? [])
  const permissions = computed(() => user.value?.permissions ?? [])
  // เครื่องมือของผู้ดูแลระบบ (เช่นปุ่มทดสอบส่งเมล) — ใช้ซ่อน UI เท่านั้น
  // การกันสิทธิ์จริงอยู่ที่ backend
  const isSuperuser = computed(() => user.value?.is_superuser === true)
  const email = computed(() => user.value?.email ?? '')

  async function login(username: string, password: string) {
    loading.value = true
    error.value = null
    try {
      await apiLogin(username, password)
      token.value = getAccessToken()
      await loadProfile()
    } finally {
      loading.value = false
    }
  }

  // ดึง role/permission/station ที่ user มองเห็น — เรียกหลัง login และตอน
  // refresh หน้า (ถ้ามี token อยู่แล้ว) ไม่ให้ล้ม flow ถ้าเส้นนี้ error
  async function loadProfile() {
    if (!token.value) return
    try {
      user.value = await fetchRolePermission()
    } catch {
      user.value = null
    }
  }

  function logout() {
    apiLogout()
    token.value = null
    user.value = null
  }

  function hasPermission(code: string): boolean {
    return permissions.value.includes(code)
  }

  return {
    token,
    user,
    loading,
    error,
    isAuthenticated,
    roles,
    permissions,
    isSuperuser,
    email,
    login,
    loadProfile,
    logout,
    hasPermission,
  }
})
